import z from "zod";
import { ingredientFractionSchema } from "@scoopulator/api-contracts";
import {
  ingredientDerivationSchema,
  ingredientDraftSchema,
  prepareIngredient,
} from "./preparation";
import { ingredientNumericFields } from "./units";

// Form values stay in display units. Canonical validation runs after conversion
// below, using the same draft/preparation schemas as API and server writes.
const displayNumberSchema = z.coerce.number<number>();

export const createIngredientSchema = z
  .object({
    name: ingredientDraftSchema["shape"].name,
    notes: ingredientDraftSchema["shape"].notes.removeDefault(),
    fatPct: displayNumberSchema,
    saturatedFatPct: displayNumberSchema,
    transFatPct: displayNumberSchema,
    sugarsPct: displayNumberSchema,
    lactosePct: displayNumberSchema,
    waterPct: displayNumberSchema,
    proteinPct: displayNumberSchema,
    carbPct: displayNumberSchema,
    fiberPct: displayNumberSchema,
    alcoholPct: displayNumberSchema,
    energyKcal: displayNumberSchema,
    pod: displayNumberSchema,
    pac: displayNumberSchema,
    cocoaPct: displayNumberSchema.superRefine((value, ctx) => {
      const parsed = ingredientFractionSchema.safeParse(value / 100);
      if (!parsed.success)
        for (const issue of parsed.error.issues)
          ctx.addIssue({ code: "custom", message: issue.message });
    }),
    cocoaSolidsNonfatPct: displayNumberSchema,
    cocoaFatPct: displayNumberSchema,
    emulsifierPct: displayNumberSchema,
    stabilizerPct: displayNumberSchema,
    composition: z.string().optional(),
    derivation: ingredientDerivationSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const draft = {
      ...data,
      category: "other" as const,
      aliases: [],
      dairyFatPct: 0,
    };
    for (const field of ingredientNumericFields) {
      if (field !== "energyKcal") draft[field] /= 100;
    }
    const parsed = ingredientDraftSchema.safeParse(draft);
    const issues = parsed.success
      ? prepareIngredient(parsed.data, data.derivation).issues
      : parsed.error.issues;
    for (const issue of issues)
      ctx.addIssue({
        code: "custom",
        message: issue.message,
        path: issue.path.length ? issue.path : ["composition"],
      });
  });

export type CreateIngredientFormData = z.infer<typeof createIngredientSchema>;

type NumericIngredientFormField = {
  [
    Field in keyof CreateIngredientFormData
  ]-?: CreateIngredientFormData[Field] extends number ? Field : never;
}[keyof CreateIngredientFormData];

export const NUTRITION_DERIVED_FIELDS = [
  "fatPct",
  "saturatedFatPct",
  "transFatPct",
  "sugarsPct",
  "lactosePct",
  "waterPct",
  "proteinPct",
  "carbPct",
  "fiberPct",
  "alcoholPct",
  "energyKcal",
  "pod",
  "pac",
  "cocoaFatPct",
  "cocoaSolidsNonfatPct",
] as const satisfies ReadonlyArray<NumericIngredientFormField>;

export function getDefaultIngredientFormValues(
  overrides?: Partial<CreateIngredientFormData>,
): CreateIngredientFormData {
  return {
    name: "",
    notes: "",
    fatPct: 0,
    saturatedFatPct: 0,
    transFatPct: 0,
    sugarsPct: 0,
    lactosePct: 0,
    waterPct: 0,
    proteinPct: 0,
    carbPct: 0,
    fiberPct: 0,
    alcoholPct: 0,
    energyKcal: 0,
    cocoaPct: 100,
    cocoaSolidsNonfatPct: 0,
    cocoaFatPct: 0,
    emulsifierPct: 0,
    stabilizerPct: 0,
    pod: 0,
    pac: 0,
    ...overrides,
  };
}

export type IngredientFormPayload = Omit<
  CreateIngredientFormData,
  "composition" | "cocoaPct"
>;

export function convertFormDataToIngredientPayload(
  data: CreateIngredientFormData,
): IngredientFormPayload {
  return {
    name: data.name,
    notes: data.notes,
    derivation: data.derivation,
    fatPct: data.fatPct / 100,
    saturatedFatPct: data.saturatedFatPct / 100,
    transFatPct: data.transFatPct / 100,
    sugarsPct: data.sugarsPct / 100,
    lactosePct: data.lactosePct / 100,
    waterPct: data.waterPct / 100,
    proteinPct: data.proteinPct / 100,
    carbPct: data.carbPct / 100,
    fiberPct: data.fiberPct / 100,
    alcoholPct: data.alcoholPct / 100,
    cocoaSolidsNonfatPct: data.cocoaSolidsNonfatPct / 100,
    cocoaFatPct: data.cocoaFatPct / 100,
    emulsifierPct: data.emulsifierPct / 100,
    stabilizerPct: data.stabilizerPct / 100,
    pod: data.pod / 100,
    pac: data.pac / 100,
    energyKcal: data.energyKcal,
  };
}
